import React from "react";
import { Moon, Flame, Zap, Activity, AlertTriangle, Heart } from "lucide-react";

/**
 * ReadinessCard - visual recovery & readiness panel built from the latest feedback entry.
 * Props: feedback (object) | null
 */
export default function ReadinessCard({ feedback }) {
  if (!feedback) {
    return (
      <div className="qv-card-app" data-testid="readiness-card">
        <div className="qv-card-head"><h3>Recovery & Readiness</h3></div>
        <div className="qv-empty" style={{ padding: 24 }}>
          Aún no tienes feedback registrado.<br />
          Envía tu primer feedback para ver tu estado.
        </div>
      </div>
    );
  }

  // Map metrics → 0..100 score, lower-is-better flipped
  const sleepScore = clamp01((feedback.sleep_hours || 0) / 9) * 100;          // 9h = 100%
  const energyScore = clamp01((feedback.energy || 0) / 5) * 100;              // 5/5 = 100%
  const stressScore = (1 - clamp01((feedback.stress || 0) / 5)) * 100;        // inverted
  const loadScore = (1 - clamp01(((feedback.rpe || 0) - 1) / 9)) * 100;       // inverted: low rpe = fresh
  const readiness = Math.round((sleepScore + energyScore + stressScore + loadScore) / 4);

  const tier = readinessTier(readiness);

  const metrics = [
    { key: "sleep", icon: Moon, color: "#60A5FA", label: "Sueño", value: `${feedback.sleep_hours || 0}h`, pct: sleepScore },
    { key: "energy", icon: Zap, color: "#F5C518", label: "Energía", value: `${feedback.energy || 0}/5`, pct: energyScore },
    { key: "stress", icon: Activity, color: "#A855F7", label: "Estrés", value: `${feedback.stress || 0}/5`, pct: stressScore },
    { key: "load", icon: Flame, color: "#F97316", label: "Carga (RPE)", value: `${feedback.rpe || 0}/10`, pct: loadScore },
  ];

  return (
    <div className="qv-card-app" data-testid="readiness-card">
      <div className="qv-card-head" style={{ marginBottom: 14 }}>
        <h3>Recovery & Readiness</h3>
        <span style={{ fontSize: 10, color: "#737373", letterSpacing: "0.22em", textTransform: "uppercase" }}>
          últ. {new Date(feedback.date).toLocaleDateString("es-ES", { day: "numeric", month: "short" })}
        </span>
      </div>

      {/* Big readiness ring */}
      <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 18, flexWrap: "wrap" }}>
        <Ring value={readiness} color={tier.color} />
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: 11, color: "#737373", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700 }}>
            Readiness
          </div>
          <div style={{ fontFamily: "Bebas Neue", fontSize: "2.2rem", lineHeight: 1, color: tier.color, marginTop: 4 }}>
            {tier.label}
          </div>
          <div style={{ color: "#a3a3a3", fontSize: 12, marginTop: 6 }}>
            {tier.message}
          </div>
        </div>
      </div>

      {/* Mini metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10 }}>
        {metrics.map((m) => (
          <div key={m.key} style={{ background: "#060606", border: `1px solid ${m.color}22`, padding: 12 }} data-testid={`readiness-${m.key}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, color: m.color, fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>
              <m.icon size={12} /> {m.label}
            </div>
            <div style={{ fontFamily: "Bebas Neue", fontSize: "1.5rem", color: "#fff", lineHeight: 1 }}>{m.value}</div>
            <div style={{ height: 4, background: "rgba(255,255,255,0.06)", marginTop: 8, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${m.pct}%`, background: m.color, transition: "width .4s ease" }} />
            </div>
          </div>
        ))}
      </div>

      {feedback.pain && (
        <div style={{ marginTop: 14, padding: 12, background: "rgba(225,29,46,0.06)", border: "1px solid rgba(225,29,46,0.35)", display: "flex", alignItems: "center", gap: 10, color: "#fff", fontSize: 13 }}>
          <AlertTriangle size={16} color="var(--qv-red)" />
          <div>
            <div style={{ fontSize: 10, color: "var(--qv-red)", letterSpacing: "0.22em", textTransform: "uppercase", fontWeight: 700 }}>Atención</div>
            Has reportado dolor{feedback.pain_zone ? ` en ${feedback.pain_zone}` : ""}.
          </div>
        </div>
      )}
    </div>
  );
}

function clamp01(n) { return Math.max(0, Math.min(1, n)); }

function readinessTier(score) {
  if (score >= 80) return { label: "Élite", color: "#22C55E", message: "Listo para entrenar al máximo. Aprovecha el día." };
  if (score >= 60) return { label: "Buena", color: "#EAB308", message: "Estado sólido. Mantén intensidad media-alta." };
  if (score >= 40) return { label: "Media", color: "#F97316", message: "Cuidado con la carga. Prioriza técnica y recuperación." };
  return { label: "Baja", color: "#E11D2E", message: "Recupera. Activa, movilidad y sueño hoy." };
}

function Ring({ value, color }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  const off = c * (1 - value / 100);
  return (
    <div style={{ position: "relative", width: 100, height: 100 }}>
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
        <circle
          cx="50" cy="50" r={r}
          stroke={color}
          strokeWidth="8"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={off}
          strokeLinecap="butt"
          transform="rotate(-90 50 50)"
          style={{ transition: "stroke-dashoffset .6s ease" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <div style={{ fontFamily: "Bebas Neue", fontSize: "2rem", color: "#fff", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 9, color: "#737373", letterSpacing: "0.22em", fontWeight: 700 }}>/100</div>
      </div>
    </div>
  );
}
